/**
 * EmptyState — reusable empty-state surface (V1-11).
 *
 * Replaces ad-hoc "centered grey text" empty states with a unified, more
 * inviting layout. Three slots:
 *
 *   - icon      : optional MUI icon element (rendered inside a soft circle)
 *   - title     : short, bold heading. Required.
 *   - body      : 1–2 sentences of context. Optional.
 *   - action    : optional CTA — { label, onClick, variant? }
 *   - secondary : optional secondary CTA — { label, onClick }
 *
 * Prefer a primary action when there's an obvious next step the user can
 * take from inside the empty state. Skip it when the filter/search controls
 * elsewhere on the page already serve that purpose (e.g. filtered Dashboard
 * empty: the chips above are the recovery affordance).
 */
import { Box, Typography, Button, Stack } from '@mui/material';

export default function EmptyState({ icon, title, body, action, secondary, sx = {} }) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        px: 3,
        py: { xs: 6, sm: 8 },
        maxWidth: 480,
        mx: 'auto',
        ...sx,
      }}
    >
      {icon && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'primary.50',
            color: 'primary.main',
            mb: 2,
            // The icon component itself controls its size; we hint a default.
            '& > svg': { fontSize: 32 },
          }}
        >
          {icon}
        </Box>
      )}

      <Typography variant="h6" fontWeight={600} gutterBottom>
        {title}
      </Typography>

      {body && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.55, mb: action || secondary ? 3 : 0 }}
        >
          {body}
        </Typography>
      )}

      {(action || secondary) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 1 }}
        >
          {action && (
            <Button
              variant={action.variant || 'contained'}
              size="medium"
              onClick={action.onClick}
              startIcon={action.icon}
            >
              {action.label}
            </Button>
          )}
          {secondary && (
            <Button
              variant="text"
              size="medium"
              onClick={secondary.onClick}
              color="inherit"
            >
              {secondary.label}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}
